import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import MaskedLines from "./MaskedLines";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1539278383962-a7774385fa02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZmFzaGlvbiUyMGVjb21tZXJjZXxlbnwwfHx8fDE3ODQ3MDczNjV8MA&ixlib=rb-4.1.0&q=85";

export default function Hero({ t, waLink }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-4, -1]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] pt-32 md:pt-40 pb-16 overflow-hidden spotlight"
    >
      {/* Big background chapter number */}
      <motion.div
        aria-hidden
        className="absolute -right-8 md:-right-10 top-24 md:top-40 chapter-num text-[22vw] leading-none pointer-events-none select-none opacity-70"
        style={{ opacity }}
      >
        00
      </motion.div>

      <div className="container-x relative z-10">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 text-[11px] md:text-xs uppercase tracking-[0.3em] text-[var(--text-muted)] font-display"
          data-testid="hero-eyebrow"
        >
          <span className="w-8 h-[1px] bg-[var(--accent)]" />
          {t.hero.eyebrow}
        </motion.div>

        {/* Massive kinetic heading */}
        <div className="mt-8 md:mt-10">
          <MaskedLines
            className="font-display font-black uppercase leading-[0.9] tracking-tighter text-[13vw] md:text-[10.5vw] lg:text-[9vw] text-[var(--text)]"
            as="h1"
            stagger={0.11}
            delay={0.35}
            lines={[
              <span key="l1">
                {t.hero.line1}{" "}
                <span className="font-serif-italic text-[var(--accent)] font-normal normal-case tracking-normal">
                  {t.hero.line1Italic}
                </span>
              </span>,
              <span key="l2">{t.hero.line2}</span>,
              <span
                key="l3"
                className="font-serif-italic text-[var(--text)] font-normal normal-case tracking-normal"
              >
                {t.hero.line2Italic}
              </span>,
            ]}
          />
        </div>

        {/* Grid: subline + card */}
        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-8 md:gap-12 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.9,
              duration: 0.9,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="col-span-12 md:col-span-6 text-[var(--text-muted)] text-base md:text-lg leading-relaxed max-w-xl"
            data-testid="hero-subline"
          >
            {t.hero.subline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 1.05,
              duration: 0.9,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="col-span-12 md:col-span-6 flex flex-col md:items-end gap-6"
          >
            <div className="flex flex-wrap gap-3">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-primary"
                className="btn-primary"
              >
                {t.hero.cta1} <span aria-hidden>→</span>
              </a>
              <a
                href="#work"
                data-testid="hero-cta-secondary"
                className="btn-ghost"
              >
                {t.hero.cta2}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Bottom row: floating photo + stats */}
        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-8 items-end">
          <motion.div
            style={{ y, rotate }}
            className="col-span-12 md:col-span-6 lg:col-span-5 relative photo-spot rounded-2xl overflow-hidden border border-[var(--border)]"
          >
            <img
              src={HERO_IMAGE}
              alt="Boutique ecommerce mockup"
              className="w-full h-[380px] md:h-[460px] object-cover"
              loading="eager"
            />
            <div className="absolute top-4 left-4 z-10 text-[10px] uppercase tracking-widest font-display text-[var(--text)] bg-black/40 backdrop-blur px-3 py-1 rounded-full border border-[var(--border)]">
              Live · Serat Denim
            </div>
          </motion.div>

          <div className="col-span-12 md:col-span-6 lg:col-span-7 grid grid-cols-3 gap-4 md:gap-8">
            {[
              { v: t.hero.stat1Value, l: t.hero.stat1Label },
              { v: t.hero.stat2Value, l: t.hero.stat2Label },
              { v: t.hero.stat3Value, l: t.hero.stat3Label },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 1.3 + i * 0.12,
                  duration: 0.9,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="border-t border-[var(--border)] pt-4"
                data-testid={`hero-stat-${i}`}
              >
                <div className="font-display font-black text-4xl md:text-6xl tracking-tighter text-[var(--text)]">
                  {s.v}
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)] font-display">
                  {s.l}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.6 }}
          className="mt-14 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)] font-display"
        >
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.6 }}
            className="inline-block"
          >
            ↓
          </motion.span>
          Scroll
        </motion.div>
      </div>
    </section>
  );
}
